'use client';
import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { type TokenPeriod, tokensQueries } from '@api/token';

import { BrandIcon } from '@ui/uikit/components/common/BrandIcon';
import { AreaChart, Chart, ChartFilters, defaultFilters } from '@ui/uikit/components/common/Chart';
import { Typography } from '@ui/uikit/components/common/Typography';

import { CoingeckoLink } from './CoingeckoLink/CoingeckoLink';
import { TokenPriceChange } from './TokenPriceChange';
import styles from './TokenPriceChart.module.scss';

type TokenPriceChartProps = {
  tokenIdOrSlug: string | number;
  coingeckoLink?: string;
};

export const TokenPriceChart = ({ tokenIdOrSlug, coingeckoLink }: TokenPriceChartProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<TokenPeriod>('day');

  const { data, isPending: isTokenChartLoading } = useQuery(
    tokensQueries.priceHistory(tokenIdOrSlug, selectedPeriod),
  );

  const currentPrice = data?.history?.at(-1)?.price;

  return (
    <div className={styles.chart}>
      <Chart>
        <Chart.Plot>
          <AreaChart
            data={data?.history}
            isLoading={isTokenChartLoading}
            selectedPeriod={selectedPeriod}
          />
        </Chart.Plot>
        <Chart.Controls>
          <div className={styles.controls}>
            <div className={styles.filters}>
              <ChartFilters
                filters={defaultFilters}
                selectedValue={selectedPeriod}
                onSelect={setSelectedPeriod}
              />
            </div>
            <div className={styles.token}>
              {isTokenChartLoading && !data ? (
                <div className={styles.skeleton}>
                  <div className="msq-skeleton-text w-16 h-6" />
                  <div className="msq-skeleton-text w-16 h-6" />
                </div>
              ) : (
                data && (
                  <div className={styles.token__info}>
                    <BrandIcon src={data.icon} size="medium" label={data.symbol} strong />
                    <Typography variant="caption-s">
                      <TokenPriceChange
                        percentChange={data.percentChange}
                        currentPrice={currentPrice}
                        period={selectedPeriod}
                      />
                    </Typography>
                  </div>
                )
              )}
            </div>
            <div className={styles.coingecko}>
              <CoingeckoLink
                isLoading={isTokenChartLoading}
                href={coingeckoLink}
                symbol={data?.symbol}
              />
            </div>
          </div>
        </Chart.Controls>
      </Chart>
    </div>
  );
};
